import React, { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Phone, Mail, Instagram, Linkedin, ChevronDown, MapPin, ArrowUpRight, MessageCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { NAV_LINKS, DROPDOWN_SERVICES, COMPANY_INFO } from '../constants';
import { RoutePath } from '../types';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Custom Cursor Logic
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);

  useEffect(() => {
    const updateMousePosition = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    
    const handleMouseOver = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'A' || 
        target.tagName === 'BUTTON' || 
        target.closest('a') || 
        target.closest('button') ||
        target.closest('[role="button"]') ||
        target.classList.contains('cursor-pointer')
      ) {
        setIsHovering(true);
      } else {
        setIsHovering(false);
      }
    };

    window.addEventListener('mousemove', updateMousePosition);
    window.addEventListener('mouseover', handleMouseOver);
    
    return () => {
      window.removeEventListener('mousemove', updateMousePosition);
      window.removeEventListener('mouseover', handleMouseOver);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    setIsMenuOpen(false);
  }, [location.pathname]);

  // Prevent scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isMenuOpen]);

  return (
    <div className="min-h-screen flex flex-col font-sans bg-transparent text-slate-200 overflow-x-hidden selection:bg-gold-400 selection:text-black overflow-y-auto">
      
      {/* Custom Cursor Element */}
      <div 
        className={`cursor-dot ${isHovering ? 'hover' : ''}`} 
        style={{ 
          left: `${mousePosition.x}px`, 
          top: `${mousePosition.y}px` 
        }} 
      />

      {/* --- FLOATING CALL BUTTON (CTA) --- */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-4 items-end">
        

         {/* Main Call Button with Pulse */}
         <motion.a
            href={`tel:${COMPANY_INFO.phone}`}
            initial={{ width: 60 }}
            whileHover={{ width: 'auto' }}
            className="h-14 bg-gold-500 rounded-full flex items-center shadow-xl shadow-gold-500/20 animate-pulse-gold overflow-hidden group cursor-pointer"
         >
            <div className="w-14 h-14 flex items-center justify-center flex-shrink-0 bg-gold-400 text-dark-950">
               <Phone size={24} fill="currentColor" />
            </div>
            <span className="whitespace-nowrap w-0 overflow-hidden group-hover:w-auto group-hover:px-4 transition-all duration-300 text-dark-950 font-bold uppercase tracking-widest text-sm">
               Appeler
            </span>
         </motion.a>
      </div>

      {/* --- ULTRA DESIGN NAVBAR --- */}
      <motion.nav 
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className={`fixed w-full z-50 transition-all duration-500 flex justify-center ${
          isScrolled ? 'pt-4' : 'pt-0 lg:pt-8'
        }`}
      >
        <div 
          className={`
            relative px-8 transition-all duration-500 flex justify-between items-center
            ${isScrolled 
              ? 'w-[95%] lg:w-[85%] bg-dark-800/80 backdrop-blur-xl border border-white/10 rounded-full shadow-2xl shadow-black/50 py-3' 
              : 'w-full bg-gradient-to-b from-black/80 to-transparent py-6 container mx-auto rounded-full lg:border-none'
            }
          `}
        >
          {/* Logo Section */}
          <NavLink to={RoutePath.HOME} className="flex flex-col group cursor-pointer relative z-50">
            <div className="flex items-center gap-3">
             
               <div className="flex flex-col">
                  <span className={`font-serif font-bold tracking-widest uppercase transition-colors duration-500 ${isScrolled ? 'text-lg text-white' : 'text-2xl text-white'}`}>
                    Limohass
                  </span>
                 
               </div>
            </div>
          </NavLink>

          {/* Right Section: Button + Menu */}
          <div className="flex items-center gap-6">
            
            {/* CTA Button (Visible on all screens larger than small mobile) */}
            <div className="hidden sm:block">
              <motion.button 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate(RoutePath.BOOKING)}
                className={`
                  relative overflow-hidden group px-6 py-2.5 rounded-full transition-all duration-300
                  ${isScrolled ? 'bg-gold-500 text-black' : 'bg-white text-black hover:bg-gold-500'}
                `}
              >
                <span className="relative text-xs font-bold uppercase tracking-widest flex items-center gap-2">
                  Réserver <ArrowUpRight size={14} />
                </span>
              </motion.button>
            </div>

            {/* Universal Menu Toggle */}
            <button 
              className={`group flex items-center gap-3 focus:outline-none z-50 relative p-2 ${isScrolled ? 'text-white' : 'text-white'}`}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              <span className="hidden md:block text-xs font-bold uppercase tracking-[0.2em] group-hover:text-gold-500 transition-colors">Menu</span>
              <div className="space-y-1.5 p-1">
                 <span className={`block w-8 h-0.5 bg-current transition-all duration-300 ${isMenuOpen ? 'rotate-45 translate-y-2 bg-gold-400' : 'bg-current'}`}></span>
                 <span className={`block w-6 h-0.5 bg-current ml-auto transition-all duration-300 ${isMenuOpen ? 'opacity-0' : 'group-hover:w-8'}`}></span>
                 <span className={`block w-8 h-0.5 bg-current transition-all duration-300 ${isMenuOpen ? '-rotate-45 -translate-y-2 bg-gold-400' : 'bg-current'}`}></span>
              </div>
            </button>
          </div>
        </div>

        {/* Full Screen Menu Overlay */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div 
              initial={{ opacity: 0, y: "-100%" }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: "-100%" }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="fixed inset-0 z-[60] bg-[#080808] flex flex-col pt-32 pb-12 px-8 overflow-y-auto rounded-none"
            >
              {/* Close Button X */}
              <button 
                onClick={() => setIsMenuOpen(false)}
                className="absolute top-8 right-8 z-50 p-4 group"
                aria-label="Fermer le menu"
              >
                 <div className="relative w-12 h-12 flex items-center justify-center border border-white/20 rounded-full group-hover:border-gold-500 transition-colors">
                    <X size={24} strokeWidth={1} className="text-white group-hover:text-gold-500 transition-colors" />
                 </div>
                 <span className="absolute top-full left-1/2 -translate-x-1/2 mt-2 text-[10px] uppercase tracking-widest text-slate-500 group-hover:text-white transition-colors">Fermer</span>
              </button>

              <div className="container mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 h-full items-center">
                
                {/* Navigation Links */}
                <div className="flex flex-col space-y-6">
                  {/* Manually building list to include Services */}
                  <div className="flex flex-col gap-2">
                    <span className="text-xs text-gold-500 uppercase tracking-widest mb-2 font-mono">Navigation</span>
                    <NavLink
                        to={RoutePath.HOME}
                        onClick={() => setIsMenuOpen(false)}
                        className={({ isActive }) => `text-4xl md:text-6xl font-serif font-light transition-all hover:pl-4 hover:text-white ${isActive ? 'text-gold-400 italic pl-4' : 'text-slate-400'}`}
                    >
                      Accueil
                    </NavLink>
                    <NavLink
                        to={RoutePath.FLEET}
                        onClick={() => setIsMenuOpen(false)}
                        className={({ isActive }) => `text-4xl md:text-6xl font-serif font-light transition-all hover:pl-4 hover:text-white ${isActive ? 'text-gold-400 italic pl-4' : 'text-slate-400'}`}
                    >
                      Flotte
                    </NavLink>
                    <NavLink
                        to={RoutePath.SERVICES}
                        onClick={() => setIsMenuOpen(false)}
                        className={({ isActive }) => `text-4xl md:text-6xl font-serif font-light transition-all hover:pl-4 hover:text-white ${isActive ? 'text-gold-400 italic pl-4' : 'text-slate-400'}`}
                    >
                      Services
                    </NavLink>
                    <NavLink
                        to={RoutePath.LAUSANNE}
                        onClick={() => setIsMenuOpen(false)}
                        className={({ isActive }) => `text-4xl md:text-6xl font-serif font-light transition-all hover:pl-4 hover:text-white ${isActive ? 'text-gold-400 italic pl-4' : 'text-slate-400'}`}
                    >
                      Lausanne
                    </NavLink>
                    <NavLink
                        to={RoutePath.ABOUT}
                        onClick={() => setIsMenuOpen(false)}
                        className={({ isActive }) => `text-4xl md:text-6xl font-serif font-light transition-all hover:pl-4 hover:text-white ${isActive ? 'text-gold-400 italic pl-4' : 'text-slate-400'}`}
                    >
                      À Propos
                    </NavLink>
                    <NavLink
                        to={RoutePath.CONTACT}
                        onClick={() => setIsMenuOpen(false)}
                        className={({ isActive }) => `text-4xl md:text-6xl font-serif font-light transition-all hover:pl-4 hover:text-white ${isActive ? 'text-gold-400 italic pl-4' : 'text-slate-400'}`}
                    >
                      Contact
                    </NavLink>
                  </div>
                </div>

                {/* Info / Decoration (Hidden on mobile landscape/small screens usually, but nice on desktop) */}
                <div className="hidden lg:flex flex-col justify-center h-full border-l border-white/10 pl-16">
                   <div className="mb-12">
                      <span className="text-xs text-gold-500 uppercase tracking-widest mb-4 block font-mono">Contact Rapide</span>
                      <a href={`tel:${COMPANY_INFO.phone}`} className="text-4xl font-serif text-white hover:text-gold-400 transition-colors block mb-2">{COMPANY_INFO.phone}</a>
                      <a href={`mailto:${COMPANY_INFO.email}`} className="text-xl text-slate-400 hover:text-white transition-colors">{COMPANY_INFO.email}</a>
                   </div>
                   
                   <div className="grid grid-cols-2 gap-8">
                      <div>
                        <h4 className="text-white font-bold uppercase tracking-widest mb-4 text-sm">Transferts</h4>
                        <ul className="space-y-2 text-slate-400">
                           <li>Genève Aéroport</li>
                           <li>Gstaad & Verbier</li>
                           <li>Montreux Riviera</li>
                        </ul>
                      </div>
                      <div>
                        <h4 className="text-white font-bold uppercase tracking-widest mb-4 text-sm">Flotte</h4>
                        <ul className="space-y-2 text-slate-400">
                           <li>Mercedes Classe S</li>
                           <li>Mercedes Classe E</li>
                           <li>Mercedes Classe V</li>
                        </ul>
                      </div>
                   </div>

                   <button 
                     onClick={() => { setIsMenuOpen(false); navigate(RoutePath.BOOKING); }}
                     className="mt-12 bg-white text-black px-8 py-4 uppercase tracking-widest font-bold hover:bg-gold-500 transition-colors self-start"
                   >
                     Réserver un chauffeur
                   </button>
                </div>

              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>

      {/* Main Content */}
      <main className="flex-grow pt-0">
        {children}
      </main>

      {/* --- NEW GIGA-INSPIRED FOOTER --- */}
      <footer className="bg-[#000000] pt-24 pb-8 relative overflow-hidden font-sans">
        <div className="container mx-auto px-6 relative z-10">
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8 mb-40">
            
            {/* Column 1: TRANSFERTS */}
            <div className="flex flex-col">
              <div className="h-[1px] w-full bg-[#333] mb-6 relative overflow-hidden">
                <div className="absolute right-0 top-0 h-full w-20 bg-gradient-to-l from-black to-transparent"></div>
              </div>
              <h4 className="text-white text-[10px] font-bold uppercase tracking-[0.2em] mb-8">Transferts</h4>
              <ul className="space-y-4">
                <li><NavLink to={RoutePath.TRANSFERS} className="text-[#888] hover:text-white transition-colors text-sm">Genève Aéroport (GVA)</NavLink></li>
                <li><NavLink to={RoutePath.LAUSANNE} className="text-[#888] hover:text-white transition-colors text-sm">Lausanne</NavLink></li>
                <li><NavLink to={RoutePath.TRANSFERS} className="text-[#888] hover:text-white transition-colors text-sm">Zürich</NavLink></li>

              </ul>
            </div>

            {/* Column 2: FLOTTE */}
            <div className="flex flex-col">
              <div className="h-[1px] w-full bg-[#333] mb-6 relative overflow-hidden">
                <div className="absolute right-0 top-0 h-full w-20 bg-gradient-to-l from-black to-transparent"></div>
              </div>
              <h4 className="text-white text-[10px] font-bold uppercase tracking-[0.2em] mb-8">Flotte</h4>
              <ul className="space-y-4">
                <li><NavLink to={RoutePath.FLEET} className="text-[#888] hover:text-white transition-colors text-sm">Mercedes Classe S</NavLink></li>
                <li><NavLink to={RoutePath.FLEET} className="text-[#888] hover:text-white transition-colors text-sm">Mercedes Classe E</NavLink></li>
                <li><NavLink to={RoutePath.FLEET} className="text-[#888] hover:text-white transition-colors text-sm">Mercedes Classe V</NavLink></li>
              </ul>
            </div>

            {/* Column 3: SERVICES */}
            <div className="flex flex-col">
              <div className="h-[1px] w-full bg-[#333] mb-6 relative overflow-hidden">
                 <div className="absolute right-0 top-0 h-full w-20 bg-gradient-to-l from-black to-transparent"></div>
              </div>
              <h4 className="text-white text-[10px] font-bold uppercase tracking-[0.2em] mb-8">Services</h4>
              <ul className="space-y-4">
                <li><NavLink to={RoutePath.SERVICES} className="text-[#888] hover:text-white transition-colors text-sm">Mise à disposition</NavLink></li>
          
                <li><NavLink to={RoutePath.SERVICES} className="text-[#888] hover:text-white transition-colors text-sm">Mariages & Événements</NavLink></li>
                <li><NavLink to={RoutePath.SERVICES} className="text-[#888] hover:text-white transition-colors text-sm">Service Diplomatique</NavLink></li>
              </ul>
            </div>

            {/* Column 4: CONTACT INFO */}
            <div className="flex flex-col">
              <div className="h-[1px] w-full bg-[#333] mb-6 relative overflow-hidden">
                 <div className="absolute right-0 top-0 h-full w-20 bg-gradient-to-l from-black to-transparent"></div>
              </div>
              <h4 className="text-white text-[10px] font-bold uppercase tracking-[0.2em] mb-8">Coordonnées</h4>
              
              <div className="space-y-6">
                <div>
                    <span className="text-[#555] text-[10px] uppercase tracking-widest block mb-2">Siège Social</span>
                    <p className="text-[#888] text-sm leading-relaxed max-w-[200px]">{COMPANY_INFO.address}</p>
                </div>
                
                <div>
                    <span className="text-[#555] text-[10px] uppercase tracking-widest block mb-2">Téléphone</span>
                    <a href={`tel:${COMPANY_INFO.phone}`} className="text-white text-lg font-serif hover:text-gold-500 transition-colors block">{COMPANY_INFO.phone}</a>
                </div>

                <div>
                    <span className="text-[#555] text-[10px] uppercase tracking-widest block mb-2">Email</span>
                    <a href={`mailto:${COMPANY_INFO.email}`} className="text-[#888] text-sm hover:text-white transition-colors">{COMPANY_INFO.email}</a>
                </div>
              </div>
            </div>

          </div>

          {/* Bottom Area with Big Text and Socials */}
          <div className="relative border-t border-[#111] pt-8 flex flex-col md:flex-row items-end justify-between">
            
            {/* GIANT WATERMARK TEXT - VISIBILITY INCREASED */}
            <div className="absolute bottom-0 left-0 -mb-8 -ml-4 md:-ml-8 select-none pointer-events-none z-0">
               <span className="font-sans font-black text-[15vw] leading-none text-white opacity-[0.12] block tracking-tighter">LIMOHASS</span>
            </div>

            <div className="relative z-10 w-full flex justify-end">
                <div className="flex flex-col items-end gap-8">
                     
                    <div className="text-right space-y-1">
                        <p className="text-[#444] text-xs">&copy; 2026 Limohass. All rights reserved.</p>
                        <p className="text-[#444] text-xs"><a href="https://performancedigital.ch/"> Site by <span className="text-[#666]">PerformanceDigital</span></a></p>
                    </div>
                </div>
            </div>
          </div>

        </div>
      </footer>
    </div>
  );
};

export default Layout;